import React from 'react';
import { CheckCircle, Target, BarChart, Activity, PieChart, Layers, Table } from 'lucide-react';

interface ExecutiveReportProps {
    content: string;
}

/**
 * Renders AI responses in an executive report format
 * Formats markdown-like content into professional sections with visual hierarchy
 */
export const ExecutiveReport: React.FC<ExecutiveReportProps> = ({ content }) => {
    // Type safety: Ensure content is a string
    const safeContent = typeof content === 'string' ? content : String(content || 'No response');

    // Parse the content into sections
    const sections = parseExecutiveContent(safeContent);

    return (
        <div className="executive-report space-y-6">
            {sections.map((section, index) => (
                <Section key={index} section={section} />
            ))}
        </div>
    );
};

// Section types
type SectionType = 'summary' | 'findings' | 'metrics' | 'table' | 'list' | 'recommendation' | 'analysis' | 'text';

interface ParsedSection {
    type: SectionType;
    title?: string;
    content: string | string[] | any;
    level?: number;
    rows?: string[][]; // For tables
    headers?: string[]; // For tables
}

// Parse content into structured sections
function parseExecutiveContent(content: string): ParsedSection[] {
    // Safety check
    if (!content || typeof content !== 'string') {
        return [{ type: 'text', content: String(content || 'No content') }];
    }

    // Strip markdown code fences
    let cleanContent = content.trim();
    if (cleanContent.startsWith('```markdown')) {
        cleanContent = cleanContent.replace(/^```markdown\s*\n/, '').replace(/\n```\s*$/, '');
    } else if (cleanContent.startsWith('```')) {
        cleanContent = cleanContent.replace(/^```\s*\n/, '').replace(/\n```\s*$/, '');
    }

    const lines = cleanContent.split('\n');
    const sections: ParsedSection[] = [];
    let currentSection: ParsedSection | null = null;
    let currentList: string[] = [];
    let currentTable: string[][] = [];

    const flushCurrent = () => {
        if (currentTable.length > 0) {
            // Process table
            // First row might be header
            let headers: string[] = [];
            let rows = currentTable;

            // Check if second row is separator |---|---|
            if (rows.length > 1 && rows[1].every(cell => /^[-:\s]+$/.test(cell))) {
                headers = rows[0];
                rows = rows.slice(2);
            } else if (rows.length > 0) {
                headers = rows[0];
                rows = rows.slice(1);
            }

            if (currentSection) {
                sections.push(currentSection);
            }
            // Create new table section (or append if special handling needed, but usually standalone)
            sections.push({
                type: 'table',
                title: currentSection?.title ? `${currentSection.title} (Table)` : undefined,
                content: '',
                headers,
                rows
            });
            currentTable = [];
            currentSection = null; // Table breaks the flow usually
            return;
        }

        if (currentList.length > 0) {
            if (currentSection) {
                // If section exists, append list? Or separate?
                // Let's make separate list section if strict typing, or add to content if flexible.
                // For this structure, let's append separate list section if currentSection has content already
                if (currentSection.content) {
                    sections.push(currentSection);
                    sections.push({ type: 'list', content: [...currentList] });
                } else {
                    currentSection.type = 'list';
                    currentSection.content = [...currentList];
                    sections.push(currentSection);
                }
            } else {
                sections.push({ type: 'list', content: [...currentList] });
            }
            currentList = [];
            currentSection = null;
            return;
        }

        if (currentSection) {
            sections.push(currentSection);
            currentSection = null;
        }
    };

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();

        // Skip empty lines in general, but might break lists/tables
        if (!line) {
            flushCurrent();
            continue;
        }

        // Table Detection (| ... |)
        if (line.startsWith('|') && line.endsWith('|')) {
            const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx !== 0 && idx !== arr.length - 1);
            currentTable.push(cells);
            continue;
        }

        // Headers
        if (line.startsWith('##') || (line.startsWith('**') && line.includes(':') && line.length < 50)) {
            flushCurrent();
            const title = line.replace(/^#+\s*/, '').replace(/\*\*/g, '').replace(':', '').trim();
            currentSection = detectSectionType(title, '');
            currentSection.title = title;
            continue;
        }

        // List items
        if (line.startsWith('- ') || line.startsWith('• ') || /^\d+\.\s/.test(line)) {
            // If we have text content in current section, flush it first?
            // Actually, keep text separate from list
            const item = line.replace(/^[-•]\s*/, '').replace(/^\d+\.\s*/, '');
            currentList.push(item);
            continue;
        }

        // Comma-separated lists (like in Pareto Analysis sometimes)
        if (line.includes(':') && line.split(',').length > 2 && currentSection?.type === 'analysis') {
            // Handle as text for now, maybe custom parsing later
        }

        // Regular Text
        if (currentSection) {
            if (typeof currentSection.content === 'string') {
                currentSection.content += (currentSection.content ? '\n' : '') + line;
            } else {
                // Should not happen if logic is sound, but reset if needed
                currentSection.content = line;
            }
        } else {
            currentSection = { type: 'text', content: line };
        }
    }

    flushCurrent();
    return sections;
}

function detectSectionType(title: string, content: string): ParsedSection {
    const lower = title.toLowerCase();

    if (lower.includes('summary') || lower.includes('overview')) return { type: 'summary', content };
    if (lower.includes('recommendation') || lower.includes('action')) return { type: 'recommendation', content };
    if (lower.includes('finding') || lower.includes('key point')) return { type: 'findings', content };
    if (lower.includes('metric') || lower.includes('kpi')) return { type: 'metrics', content };
    if (lower.includes('pareto') || lower.includes('analysis') || lower.includes('matrix') || lower.includes('feedback') || lower.includes('growth')) return { type: 'analysis', content };

    return { type: 'text', content };
}

const Section: React.FC<{ section: ParsedSection }> = ({ section }) => {
    switch (section.type) {
        case 'summary': return <SummarySection section={section} />;
        case 'findings': return <FindingsSection section={section} />;
        case 'metrics': return <MetricsSection section={section} />;
        case 'recommendation': return <RecommendationSection section={section} />;
        case 'analysis': return <AnalysisSection section={section} />;
        case 'table': return <TableSection section={section} />;
        case 'list': return <ListSection section={section} />;
        default: return <TextSection section={section} />;
    }
};

// --- Section Components ---

const SummarySection: React.FC<{ section: ParsedSection }> = ({ section }) => (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-l-4 border-blue-500 p-6 rounded-r-lg">
        <Header icon={<BarChart className="w-5 h-5 text-white" />} title={section.title || 'Executive Summary'} color="blue" />
        <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {formatText(section.content as string)}
        </div>
    </div>
);

const FindingsSection: React.FC<{ section: ParsedSection }> = ({ section }) => (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-l-4 border-amber-500 p-6 rounded-r-lg">
        <Header icon={<Target className="w-5 h-5 text-white" />} title={section.title || 'Key Findings'} color="amber" />
        <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {formatText(section.content as string)}
        </div>
    </div>
);

const RecommendationSection: React.FC<{ section: ParsedSection }> = ({ section }) => (
    <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-l-4 border-green-500 p-6 rounded-r-lg">
        <Header icon={<CheckCircle className="w-5 h-5 text-white" />} title={section.title || 'Recommendations'} color="green" />
        <div className="text-gray-700 dark:text-gray-300 leading-relaxed">
            {formatText(section.content as string)}
        </div>
    </div>
);

const AnalysisSection: React.FC<{ section: ParsedSection }> = ({ section }) => {
    const title = section.title || 'Analysis';
    const isPareto = title.toLowerCase().includes('pareto');
    const isMatrix = title.toLowerCase().includes('matrix');

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                {isPareto ? <PieChart className="w-5 h-5 text-purple-500" /> :
                    isMatrix ? <Layers className="w-5 h-5 text-indigo-500" /> :
                        <Activity className="w-5 h-5 text-blue-500" />}
                {title}
            </h3>
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-2">
                {formatText(section.content as string)}
            </div>
        </div>
    );
};

const MetricsSection: React.FC<{ section: ParsedSection }> = ({ section }) => {
    // Simple parsing for Key: Value metrics if not already handled by general parser
    const content = section.content as string;
    // ... logic remains similar to previous version, ensuring display
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{section.title}</h3>
            <div className="text-gray-700 dark:text-gray-300">{formatText(content)}</div>
        </div>
    );
};

const TableSection: React.FC<{ section: ParsedSection }> = ({ section }) => {
    const { headers, rows } = section;
    if (!headers || !rows) return null;

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm my-4">
            {section.title && (
                <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Table className="w-4 h-4 text-gray-500" />
                        {section.title.replace('(Table)', '')}
                    </h3>
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-300">
                        <tr>
                            {headers.map((header, i) => (
                                <th key={i} className="px-6 py-3 font-bold tracking-wider">{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {rows.map((row, i) => (
                            <tr key={i} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                {row.map((cell, j) => (
                                    <td key={j} className="px-6 py-4 font-medium text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                        {formatCellContent(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ListSection: React.FC<{ section: ParsedSection }> = ({ section }) => {
    const items = section.content as string[];
    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
            {section.title && <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{section.title}</h3>}
            <ul className="space-y-3">
                {items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300">{formatInlineText(item)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

const TextSection: React.FC<{ section: ParsedSection }> = ({ section }) => (
    <div className="mb-4">
        {section.title && <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{section.title}</h3>}
        <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-base">
            {formatText(section.content as string)}
        </div>
    </div>
);

// --- Helpers ---

const Header: React.FC<{ icon: React.ReactNode, title: string, color: string }> = ({ icon, title, color }) => {
    const bgColors: Record<string, string> = {
        blue: 'bg-blue-500', amber: 'bg-amber-500', green: 'bg-green-500'
    };
    const textColors: Record<string, string> = {
        blue: 'text-blue-900 dark:text-blue-100', amber: 'text-amber-900 dark:text-amber-100', green: 'text-green-900 dark:text-green-100'
    };

    return (
        <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${bgColors[color] || 'bg-gray-500'}`}>
                {icon}
            </div>
            <h2 className={`text-xl font-bold ${textColors[color] || 'text-gray-900'}`}>
                {title}
            </h2>
        </div>
    );
};

function formatCellContent(text: string): React.ReactNode {
    // Check for RPN numbers to color code
    if (/^RPN:?\s*\d+$/.test(text) || /^\d+$/.test(text)) {
        const num = parseInt(text.replace(/\D/g, ''));
        if (!isNaN(num)) {
            let colorClass = 'text-green-600 bg-green-50 dark:bg-green-900/20'; // Low risk
            if (num > 100) colorClass = 'text-red-600 bg-red-50 dark:bg-red-900/20 font-bold'; // High risk
            else if (num > 50) colorClass = 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'; // Medium risk

            return (
                <span className={`px-2 py-1 rounded ${colorClass}`}>
                    {text}
                </span>
            );
        }
    }
    return formatInlineText(text);
}


function formatText(text: string | any): React.ReactNode {
    const safeText = typeof text === 'string' ? text : String(text || '');
    return safeText.split('\n').map((line, idx) => (
        <p key={idx} className="mb-2 last:mb-0">
            {formatInlineText(line)}
        </p>
    ));
}

function formatInlineText(text: any): React.ReactNode {
    if (!text || typeof text !== 'string') return String(text || '');

    const parts: React.ReactNode[] = [];
    let remaining = text;
    let partKey = 0;

    // Regex for: **bold**, *italic*, `code`
    // We process sequentially finding the earliest match
    while (remaining.length > 0) {
        // Find earliest occurrence of delimiters
        const nextBold = remaining.indexOf('**');
        const nextItalic = remaining.indexOf('*'); // check it's not part of **
        const nextCode = remaining.indexOf('`');

        // Filter valid start positions
        const indices = [
            { type: 'bold', idx: nextBold },
            { type: 'italic', idx: nextItalic },
            { type: 'code', idx: nextCode }
        ].filter(x => x.idx !== -1).sort((a, b) => a.idx - b.idx);

        if (indices.length === 0) {
            parts.push(remaining);
            break;
        }

        const first = indices[0];

        // Push text before match
        if (first.idx > 0) {
            parts.push(remaining.substring(0, first.idx));
            remaining = remaining.substring(first.idx);
        }

        // Process matching part
        if (first.type === 'bold') {
            const end = remaining.indexOf('**', 2);
            if (end !== -1) {
                parts.push(<strong key={partKey++} className="font-bold text-slate-900 dark:text-white">{remaining.substring(2, end)}</strong>);
                remaining = remaining.substring(end + 2);
            } else {
                parts.push('**'); // unmatched
                remaining = remaining.substring(2);
            }
        } else if (first.type === 'italic') {
            const end = remaining.indexOf('*', 1);
            if (end !== -1) {
                parts.push(<em key={partKey++} className="italic text-slate-800 dark:text-slate-200">{remaining.substring(1, end)}</em>);
                remaining = remaining.substring(end + 1);
            } else {
                parts.push('*');
                remaining = remaining.substring(1);
            }
        } else if (first.type === 'code') {
            const end = remaining.indexOf('`', 1);
            if (end !== -1) {
                parts.push(<code key={partKey++} className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-sm font-mono text-pink-600 dark:text-pink-400">{remaining.substring(1, end)}</code>);
                remaining = remaining.substring(end + 1);
            } else {
                parts.push('`');
                remaining = remaining.substring(1);
            }
        }
    }
    return <>{parts}</>;
}

export default ExecutiveReport;
